import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Grid3X3,
  Package,
  Euro,
  ShoppingCart,
  Pencil,
  Plus,
  Settings,
  Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { motion } from "framer-motion";

const VendingPage = () => {
  const [compartments, setCompartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedCompartment, setSelectedCompartment] = useState(null);
  const [saleQuantity, setSaleQuantity] = useState("1");
  
  // Edit form
  const [editForm, setEditForm] = useState({
    product_id: "",
    max_capacity: "10"
  });
  
  // Add form
  const [addForm, setAddForm] = useState({
    slot_id: "",
    product_id: "",
    max_capacity: "10"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [compRes, prodRes] = await Promise.all([
        axios.get(`${API}/compartments`),
        axios.get(`${API}/products`)
      ]);
      setCompartments(compRes.data);
      setProducts(prodRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const openSaleDialog = (compartment) => {
    if (!compartment.product_id || compartment.current_stock === 0) {
      toast.error("Fach ist leer");
      return;
    }
    setSelectedCompartment(compartment);
    setSaleQuantity("1");
    setSaleDialogOpen(true);
  };

  const openEditDialog = (compartment) => {
    setSelectedCompartment(compartment);
    setEditForm({
      product_id: compartment.product_id || "",
      max_capacity: String(compartment.max_capacity || 10)
    });
    setEditDialogOpen(true);
  };

  const openAddDialog = () => {
    // Find next available slot
    const existingSlots = compartments.map(c => c.slot_id);
    const rows = ["A", "B", "C", "D", "E"];
    let nextSlot = "A1";
    
    for (const row of rows) {
      for (let i = 1; i <= 10; i++) {
        const slot = `${row}${i}`;
        if (!existingSlots.includes(slot)) {
          nextSlot = slot;
          break;
        }
      }
    }
    
    setAddForm({
      slot_id: nextSlot,
      product_id: "",
      max_capacity: "10"
    });
    setAddDialogOpen(true);
  };

  const handleSale = async () => {
    if (!selectedCompartment) return;
    
    const qty = parseInt(saleQuantity) || 1;
    if (qty <= 0 || qty > selectedCompartment.current_stock) {
      toast.error("Ungültige Menge");
      return;
    }

    try {
      await axios.post(`${API}/sales`, {
        compartment_id: selectedCompartment.id,
        quantity: qty
      });

      toast.success(`${qty}x ${selectedCompartment.product_name} verkauft`);
      setSaleDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Verkauf");
    }
  };

  const handleEdit = async () => {
    if (!selectedCompartment) return;

    try {
      await axios.put(`${API}/compartments/${selectedCompartment.id}`, {
        product_id: editForm.product_id || null,
        max_capacity: parseInt(editForm.max_capacity) || 10
      });

      toast.success("Fach aktualisiert");
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleAdd = async () => {
    if (!addForm.slot_id) {
      toast.error("Bitte Fach-ID eingeben");
      return;
    }

    try {
      await axios.post(`${API}/compartments`, {
        slot_id: addForm.slot_id,
        product_id: addForm.product_id || null,
        max_capacity: parseInt(addForm.max_capacity) || 10
      });

      toast.success("Fach hinzugefügt");
      setAddDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "empty": return "border-red-300 bg-red-50";
      case "low": return "border-amber-300 bg-amber-50";
      case "full": return "border-emerald-300 bg-emerald-50";
      default: return "border-slate-200 bg-white";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  // Group compartments by row
  const groupedCompartments = compartments.reduce((acc, comp) => {
    const row = comp.slot_id?.[0] || "?";
    if (!acc[row]) acc[row] = [];
    acc[row].push(comp);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vending-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Automat
          </h1>
          <p className="text-slate-500 mt-1">
            {compartments.length} Fächer • Klicken zum Verkaufen
          </p>
        </div>
        <Button
          onClick={openAddDialog}
          variant="outline"
          data-testid="add-compartment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Fach hinzufügen
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-emerald-300 bg-emerald-50"></div>
          <span>Voll</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-slate-200 bg-white"></div>
          <span>OK</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-amber-300 bg-amber-50"></div>
          <span>Niedrig (&lt;30%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-red-300 bg-red-50"></div>
          <span>Leer</span>
        </div>
      </div>

      {/* Compartments Grid */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Grid3X3 className="w-5 h-5" />
            Automaten-Fächer
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(groupedCompartments).length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              Keine Fächer konfiguriert. Klicken Sie auf "Fach hinzufügen".
            </p>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedCompartments).sort().map(([row, comps]) => (
                <div key={row}>
                  <p className="text-sm font-medium text-slate-500 mb-3">Reihe {row}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                    {comps.sort((a, b) => a.slot_id?.localeCompare(b.slot_id)).map((comp) => (
                      <motion.div
                        key={comp.id}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className={`relative rounded-xl border-2 overflow-hidden cursor-pointer transition-all ${getStatusColor(comp.status)}`}
                        onClick={() => openSaleDialog(comp)}
                      >
                        {/* Slot Badge */}
                        <Badge className="absolute top-2 left-2 bg-slate-700 text-xs">
                          {comp.slot_id}
                        </Badge>

                        {/* Edit Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-1 right-1 h-7 w-7 bg-white/80 hover:bg-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(comp);
                          }}
                        >
                          <Settings className="w-3 h-3" />
                        </Button>

                        {/* Content */}
                        <div className="pt-10 pb-4 px-3 text-center">
                          {comp.product_name ? (
                            <>
                              <Package className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                              <p className="font-medium text-sm truncate" title={comp.product_name}>
                                {comp.product_name}
                              </p>
                              <p className="text-xl font-bold mt-1">
                                {comp.current_stock}
                                <span className="text-sm font-normal text-slate-400">
                                  /{comp.max_capacity}
                                </span>
                              </p>
                              <p className="text-emerald-600 font-mono text-sm mt-1">
                                {comp.sale_price?.toFixed(2) || "0.00"}€
                              </p>
                              {comp.expiry_date && (
                                <div className="flex items-center justify-center gap-1 mt-1 text-xs text-slate-500">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(comp.expiry_date)}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="w-8 h-8 mx-auto mb-2 rounded-full border-2 border-dashed border-slate-300"></div>
                              <p className="text-slate-400 text-sm">Nicht belegt</p>
                            </>
                          )}
                        </div>

                        {/* Fill Bar */}
                        {comp.product_name && (
                          <div className="h-1 bg-slate-200">
                            <div
                              className={`h-full transition-all ${
                                comp.status === "empty" ? "bg-red-500" :
                                comp.status === "low" ? "bg-amber-500" :
                                comp.status === "full" ? "bg-emerald-500" :
                                "bg-blue-500"
                              }`}
                              style={{ width: `${Math.min(100, (comp.current_stock / comp.max_capacity) * 100)}%` }}
                            />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sale Dialog */}
      <Dialog open={saleDialogOpen} onOpenChange={setSaleDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Verkauf
            </DialogTitle>
          </DialogHeader>

          {selectedCompartment && (
            <div className="space-y-4 py-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-lg font-medium">{selectedCompartment.product_name}</p>
                <p className="text-3xl font-bold text-emerald-600 mt-2">
                  {selectedCompartment.sale_price?.toFixed(2) || "0.00"}€
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Fach {selectedCompartment.slot_id} • {selectedCompartment.current_stock} verfügbar
                </p>
              </div>

              <div className="space-y-2">
                <Label>Menge</Label>
                <Input
                  type="number"
                  min="1"
                  max={selectedCompartment.current_stock}
                  value={saleQuantity}
                  onChange={(e) => setSaleQuantity(e.target.value)}
                />
              </div>

              {parseInt(saleQuantity) > 0 && (
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700">Gesamt:</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {((selectedCompartment.sale_price || 0) * parseInt(saleQuantity)).toFixed(2)}€
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSaleDialogOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSale}
                >
                  <Euro className="w-4 h-4 mr-2" />
                  Verkaufen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Fach {selectedCompartment?.slot_id} bearbeiten
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Produkt zuweisen</Label>
              <Select
                value={editForm.product_id}
                onValueChange={(v) => setEditForm({ ...editForm, product_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kein Produkt" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Kein Produkt</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} - {p.sale_price?.toFixed(2) || "0.00"}€
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Maximale Kapazität</Label>
              <Input
                type="number"
                min="1"
                value={editForm.max_capacity}
                onChange={(e) => setEditForm({ ...editForm, max_capacity: e.target.value })}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setEditDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                className="flex-1 bg-rose-600 hover:bg-rose-700"
                onClick={handleEdit}
              >
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Compartment Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Neues Fach hinzufügen</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Fach-ID (z.B. A1, B2)</Label>
              <Input
                value={addForm.slot_id}
                onChange={(e) => setAddForm({ ...addForm, slot_id: e.target.value.toUpperCase() })}
                placeholder="A1"
              />
            </div>

            <div className="space-y-2">
              <Label>Produkt (optional)</Label>
              <Select
                value={addForm.product_id}
                onValueChange={(v) => setAddForm({ ...addForm, product_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Später zuweisen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Später zuweisen</SelectItem>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Maximale Kapazität</Label>
              <Input
                type="number"
                min="1"
                value={addForm.max_capacity}
                onChange={(e) => setAddForm({ ...addForm, max_capacity: e.target.value })}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setAddDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                className="flex-1 bg-rose-600 hover:bg-rose-700"
                onClick={handleAdd}
              >
                Hinzufügen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendingPage;
